import React, { useState, useEffect } from 'react';

import { Link as RouterLink, useNavigate } from 'react-router-dom';

import {
  makeStyles,
  Typography,
  Card,
  Box,
  Container,
  Link,
  Button,
  Dialog,
  FormControl,
  List,
  ListItem,
  ListItemText,
  DialogTitle,
  Checkbox,
  FormControlLabel,
  SvgIcon
} from '@material-ui/core';
import { Download as DownloadIcon } from 'react-feather';

import 'moment/locale/es';
import moment from 'moment';
import { t } from 'src/shared/literals';
import { formatNumber } from 'src/shared/support/number';

import Page from 'src/shared/ui/components/Page';
import Form from 'src/shared/ui/components/Form';
import processFormErrorMessages from 'src/shared/ui/components/Form/FormErrorMessage';

import validateSchema from 'src/shared/models/SchemaValidator';
import DailyCashClosure from 'src/daily-cash-closure/models/DailyCashClosure';

import getDailyCashClosureById from 'src/daily-cash-closure/use-cases/getDailyCashClosureById';
import updateDailyCashClosureById from 'src/daily-cash-closure/use-cases/updateDailyCashClosureById';
import getEmployeeList from 'src/employee/use-cases/getEmployeeList';
import getCompanyBankAccountList from 'src/company-bank-account/use-cases/getCompanyBankAccountList';
import uploadFile from 'src/shared/use-cases/uploadFile';
import downloadDailyCashClosureDocumentPdfByIndex from 'src/daily-cash-closure/use-cases/downloadDailyCashClosureDocumentPdfByIndex';

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: theme.palette.background.dark,
    minHeight: '100%',
    paddingBottom: theme.spacing(3),
    paddingTop: theme.spacing(3)
  },
  saveButton: {
    top: '-20px'
  },
  backButton: {
    top: '-20px',
    marginRight: theme.spacing(1)
  },
  scrollBox: {
    overflow: 'scroll',
    height: '80vh'
  },
  payments: {
    textAlign: 'center'
  },
  paymentsTable: {
    textAlign: 'center',
    margin: '5px',
    border: '1px solid'
  },
  paymentsTableRow: {
    border: '1px solid'
  },
  documentButton: {
    top: '-20px',
    marginRight: theme.spacing(1)
  },
  documentButtonRed: {
    background: 'red',
    color: 'white',
    top: '-20px',
    marginRight: theme.spacing(1)
  },
  dialogContainer: {
    width: '600px',
    minHeight: '100px'
  },
  documentsDialogContainer: {
    minHeight: '100px'
  },
  dialogListItemSecondary: {
    textAlign: 'center',
    color: 'gray',
    backgroundColor: '#fff',
    padding: '6px 16px',
    marginLeft: '10%',
    width: '80%',
    borderRadius: 5
  },
  dialogListItemPrimary: {
    textAlign: 'center',
    color: '#fff',
    backgroundColor: '#4caf50',
    padding: '6px 16px',
    width: '80%',
    marginLeft: '10%',
    marginTop: '20px',
    borderRadius: 5
  },
  formControl: {
    width: '200px',
    margin: theme.spacing(1)
  },
  selectField: {
    margin: 2,
    paddingTop: '17px'
  },
  checkboxFieldDocument: {
    margin: 1,
    paddingTop: '2px'
  },
  documentPending: {
    background: 'red',
    color: 'white'
  },
  documentUploaded: {
    background: 'green',
    color: 'white'
  },
  optionalDocumentUploaded: {
    background: 'orange',
    color: 'white'
  },
  documentTable: {
    textAlign: 'center',
    margin: '5px',
    border: '1px solid'
  },
  optionalDocumentTable: {
    textAlign: 'center',
    margin: '5px',
    border: '2px solid orange'
  },
  downloadButton: {
    color: 'blue'
  }
}));

const DOCUMENT_TYPE = 'bank_deposit_note';

const CASH_PAYMENT_TYPES = ['CASH_MONEY_COLLECTOR', 'CASH_BOX', 'BANK_CHECK'];

const DailyCashClosureForm = ({ dailyCashClosureId, cancel, success }) => {
  const classes = useStyles();
  const navigate = useNavigate();

  const companyHeadquarterId = localStorage.selectedUserHeadquarter;

  const [formFields, setFormFields] = useState([]);
  const [isRequesting, setIsRequesting] = useState(false);
  const [dailyCashClosure, setDailyCashClosure] = useState({});
  const [showDocumentsDialog, setShowDocumentsDialog] = useState(false);

  const [showDocumentUploadDialog, setShowDocumentUploadDialog] = useState(
    false
  );

  const user = JSON.parse(localStorage.user);

  const getDocumentUploadTypeIndex = () =>
    dailyCashClosure.documents && dailyCashClosure.documents.length
      ? dailyCashClosure.documents.length - 1
      : 0;

  const handleUploadDocument = async () => {
    try {
      const documentUploadTypeIndex = getDocumentUploadTypeIndex();

      if (documentUploadTypeIndex > 4) {
        alert('No se pueden adjuntar más de 5 archivos');
        return;
      }

      const documentUploadType = `${DOCUMENT_TYPE}_${documentUploadTypeIndex}`;

      let file = document.getElementById('file');
      if (!file.files || !file.files[0]) {
        alert('Debe seleccionar un archivo pdf o jpg');
        return;
      }

      file = file.files[0];

      if (
        file.type.indexOf('pdf') === -1 &&
        file.type.indexOf('jpeg') === -1 &&
        file.type.indexOf('jpg') === -1
      ) {
        alert('El formato del archivo debe ser pdf o jpg');
        return;
      }

      await uploadFile(
        `daily-cash-closures/upload/document?dailyCashClosureId=${
          dailyCashClosure.id
        }&documentType=${documentUploadType.toString()}`,
        file
      );

      let documents = dailyCashClosure.documents || [];
      const user = JSON.parse(localStorage.user);
      documents.push({
        userFullName: `${user.firstname} ${user.surnames}`,
        timestamp: new Date().getTime(),
        type: documentUploadType
      });

      let updatedDailyCashClosure = { ...dailyCashClosure };
      updatedDailyCashClosure.documents = documents;
      alert('Documento subido');
      setDailyCashClosure(updatedDailyCashClosure);

      setShowDocumentUploadDialog(false);
    } catch (e) {
      alert('Ha ocurrido un error subiendo el archivo:' + e);
      setShowDocumentUploadDialog(true);
    }
  };

  const initFormFields = async (fields, resetFields) => {
    const employeeList = getEmployeeList({
      companyHeadquarterId,
      page: null,
      limit: null,
      orderBy: ''
    });

    const companyBankAccountList = getCompanyBankAccountList({
      page: null,
      limit: null,
      orderBy: ''
    });

    Promise.all([employeeList, companyBankAccountList]).then((responseList) => {
      console.log(responseList);

      const instance = DailyCashClosure({
        fields,
        user: JSON.parse(localStorage.user),
        employeeList: responseList[0].rows,
        companyBankAccountList: responseList[1]
      });

      if (resetFields) {
        setFormFields([]);
      }
      setFormFields(instance);
    });
  };

  const fetchForm = async () => {
    let record;
    if (dailyCashClosureId) {
      record = await getDailyCashClosureById(dailyCashClosureId);
    }

    initFormFields(record);
    setDailyCashClosure(record);
  };

  useEffect(() => {
    fetchForm();
  }, []);

  const validateFormFields = async (formFieldsValues) => {
    const validatedFormFields = await validateSchema(formFieldsValues);
    formFieldsValues = processFormErrorMessages(
      validatedFormFields,
      formFieldsValues
    );

    setFormFields(formFieldsValues.fields);

    return {
      errors: formFieldsValues.errors,
      fields: validatedFormFields
    };
  };

  const handleChangeFormFieldsValues = (formFieldsValues, target) => {
    if (target.name === 'status') {
      /*
            let fields = {};
            formFieldsValues.forEach(x => {
                const item = {
                    [x.name]: x.value
                };
                fields = {
                    ...fields,
                    ...item
                };
            });

            initFormFields(fields, true);
            return;
            */
    }

    setFormFields(formFieldsValues);
    //validateFormFields(formFieldsValues);
  };

  const postForm = async () => {
    if (isRequesting) {
      return;
    }

    const validatedForm = await validateFormFields(formFields);
    if (validatedForm.errors) {
      alert('Rellene todos los campos correctamente.');
      return;
    }

    setIsRequesting(true);

    let result = await updateDailyCashClosureById(
      validatedForm.fields,
      dailyCashClosureId
    );
    setIsRequesting(false);

    if (typeof result.id === 'undefined') {
      if (t(result.error)) {
        alert(t(result.error));
      } else {
        alert('Se ha producido un error, vuelva a intentarlo');
      }
    } else {
      navigate('/app/daily-cash-closures', { replace: true });
    }
  };

  const handleCloseDocumentsDialog = () => {
    setShowDocumentsDialog(false);
  };

  const handleShowUploadDocument = () => {
    setShowDocumentUploadDialog(true);
  };

  const handleCloseUploadDocumentDialog = () => {
    setShowDocumentsDialog(false);
    setShowDocumentUploadDialog(false);
  };

  const hasDocumentType = (documentType) => {
    return (
      dailyCashClosure &&
      dailyCashClosure.documents &&
      dailyCashClosure.documents.some((document) =>
        document.type.includes(documentType)
      )
    );
  };

  const hasSomePendingDocument = () =>
    CASH_PAYMENT_TYPES.includes(dailyCashClosure.paymentTypeCode) &&
    (!dailyCashClosure.documents || Object.keys(!dailyCashClosure).length);

  const handleClickDownloadDocumentByIndex = (documentIndex) => {
    downloadDailyCashClosureDocumentPdfByIndex({
      dailyCashClosureId: dailyCashClosure.id,
      documentIndex
    });
  };

  const renderDocuments = () => {
    return (
      <table className={classes.documentTable}>
        <thead>
          <th className={classes.documentTable}>ESTADO</th>
          <th></th>

          {[0, 1, 2, 3, 4].map((documentTypeIndex) => (
            <th className={classes.documentTable}>
              {t(`${DOCUMENT_TYPE}_${documentTypeIndex}`).toUpperCase()}
            </th>
          ))}
        </thead>
        <tbody>
          <tr className={classes.documentTable}>
            {hasSomePendingDocument() ? (
              <td className={classes.documentPending}>PENDIENTE</td>
            ) : (
              <td className={classes.documentUploaded}>OK</td>
            )}
            <td></td>
            {hasDocumentType(`${DOCUMENT_TYPE}_0`) ? (
              <td className={classes.documentUploaded}>OK</td>
            ) : (
              <td className={classes.documentPending}>X</td>
            )}
          </tr>

          <tr>
            <td colSpan="9">
              <hr></hr>
            </td>
          </tr>
          <tr>
            <td></td>
          </tr>
          <tr>
            <td></td>
          </tr>
          <tr>
            <td></td>
          </tr>
          <tr>
            <td></td>
          </tr>
          <tr>
            <td></td>
          </tr>
          <tr>
            <td></td>
          </tr>
          <tr>
            <td></td>
          </tr>
          <tr>
            <td></td>
          </tr>
          <tr>
            <td></td>
          </tr>
          <tr>
            <td></td>
          </tr>
          <tr>
            <td></td>
          </tr>
          <tr>
            <td></td>
          </tr>
          <tr>
            <td></td>
          </tr>
          <tr>
            <td></td>
          </tr>
          <tr>
            <td></td>
          </tr>
          <tr>
            <td></td>
          </tr>
          <tr>
            <td></td>
          </tr>
          <tr>
            <td></td>
          </tr>
          <tr>
            <td></td>
          </tr>
          <tr>
            <td></td>
          </tr>

          {dailyCashClosure && dailyCashClosure.documents ? (
            dailyCashClosure.documents.map(
              (dailyCashClosureDocumentType, index) => (
                <tr>
                  <td className={classes.documentTable}>
                    {moment(dailyCashClosureDocumentType.timestamp)
                      .locale('es')
                      .format('DD/MM/YYYY hh:mm:ss')}
                    <br></br>
                    {dailyCashClosureDocumentType.userFullName || ''}
                  </td>
                  <td>
                    <SvgIcon
                      className={classes.downloadButton}
                      onClick={(event) =>
                        handleClickDownloadDocumentByIndex(index)
                      }
                      fontSize="small"
                      color="action"
                    >
                      <DownloadIcon />
                    </SvgIcon>
                  </td>
                  {[0, 1, 2, 3, 4].map((documentTypeIndex) =>
                    dailyCashClosureDocumentType.type.includes(
                      `${DOCUMENT_TYPE}_${documentTypeIndex}`
                    ) ? (
                      <td className={classes.documentUploaded}>OK</td>
                    ) : (
                      <td className={classes.documentTable}> </td>
                    )
                  )}
                </tr>
              )
            )
          ) : (
            <></>
          )}
        </tbody>
      </table>
    );
  };

  const renderDocumentsDialog = () => {
    return (
      <Dialog
        className={classes.documentsDialog}
        onClose={handleCloseDocumentsDialog}
        aria-labelledby="documents-dialog"
        open={showDocumentsDialog}
        maxWidth="false"
      >
        <div className={classes.documentsDialogContainer}>
          <DialogTitle id="documents-dialog">Documentos</DialogTitle>

          {showDocumentUploadDialog ? (
            <List>
              <input type="file" id="file" name="file" />
              <br />
              <ListItem
                className={classes.dialogListItemPrimary}
                button
                onClick={handleUploadDocument}
                key={'UPLOAD_DOCUMENT'}
              >
                <ListItemText primary={'Subir'} />
              </ListItem>
              <ListItem
                className={classes.dialogListItemSecondary}
                button
                onClick={handleCloseUploadDocumentDialog}
                key={'CANCEL_UPLOAD_DOCUMENT'}
              >
                <ListItemText primary={'Cancelar'} />
              </ListItem>
            </List>
          ) : (
            <>
              {renderDocuments()}

              <List>
                <ListItem
                  className={classes.dialogListItemPrimary}
                  button
                  onClick={handleShowUploadDocument}
                  key={'ADD_DOCUMENT'}
                >
                  <ListItemText primary={'Añadir documento'} />
                </ListItem>
                <ListItem
                  className={classes.dialogListItemSecondary}
                  button
                  onClick={handleCloseDocumentsDialog}
                  key={'CANCEL_ADD_DOCUMENT'}
                >
                  <ListItemText primary={'Cancelar'} />
                </ListItem>
              </List>
            </>
          )}
        </div>
      </Dialog>
    );
  };

  const handleShowDocuments = () => {
    if (
      CASH_PAYMENT_TYPES.includes(dailyCashClosure.paymentTypeCode) &&
      !dailyCashClosure.depositNumber
    ) {
      alert(
        'Debe rellenar previamente el monto, nº de boleta y guardar el documento'
      );
      return;
    }

    setShowDocumentsDialog(true);
  };

  const renderButtonList = () => {
    return (
      <Box display="flex" justifyContent="flex-end">
        <Button
          color={'secondary'}
          className={classes.backButton}
          onClick={cancel}
        >
          {'Volver'}
        </Button>

        <Button
          color={'primary'}
          className={
            hasSomePendingDocument()
              ? classes.documentButtonRed
              : classes.documentButton
          }
          onClick={handleShowDocuments}
        >
          {'Documentos'}
        </Button>
        <Button
          color="primary"
          variant="contained"
          className={classes.saveButton}
          onClick={postForm}
        >
          Guardar
        </Button>
      </Box>
    );
  };

  const renderPaymentBreakdown = () => {
    return (
      <Card className={classes.payments}>
        <h3>Desglose de pagos</h3>
        <table className={classes.paymentsTable}>
          <thead>
            <th className={classes.paymentsTableRow}>Fecha</th>
            <th className={classes.paymentsTableRow}>Nº recibo</th>
            <th className={classes.paymentsTableRow}>Nº Factura</th>
            <th className={classes.paymentsTableRow}>Monto recibo</th>
            <th className={classes.paymentsTableRow}>Monto Factura</th>
            <th className={classes.paymentsTableRow}>Restante Factura</th>
            <th className={classes.paymentsTableRow}>Referencia</th>
            <th className={classes.paymentsTableRow}>Comentarios</th>
            <th className={classes.paymentsTableRow}>Comentarios internos</th>
          </thead>
          <tbody>
            {dailyCashClosure.payments &&
              dailyCashClosure.payments.map((payment) => (
                <tr>
                  <td className={classes.paymentsTableRow}>
                    {moment(payment.datetime)
                      .locale('es')
                      .format('HH:mm:ss DD/MM/YYYY')}{' '}
                  </td>
                  <td className={classes.paymentsTableRow}>
                    {payment.number}{' '}
                  </td>
                  <td
                    className={classes.paymentsTableRow}
                  >{`${payment.invoiceStampingNumber} ${payment.invoiceNumber}`}</td>
                  <td className={classes.paymentsTableRow}>
                    {formatNumber(payment.amount)}
                  </td>
                  <td className={classes.paymentsTableRow}>
                    {formatNumber(payment.invoiceAmount)}
                  </td>
                  <td className={classes.paymentsTableRow}>
                    {formatNumber(payment.rest)}
                  </td>
                  <td className={classes.paymentsTableRow}>
                    {payment.reference}
                  </td>
                  <td className={classes.paymentsTableRow}>
                    {payment.comments}
                  </td>
                  <td className={classes.paymentsTableRow}>
                    {payment.internalComments}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </Card>
    );
  };

  const canShowPaymentBreakdown = () =>
    user.scopes.includes('admin:daily-cash-closures') ||
    dailyCashClosure.registeredAmount;

  return (
    <Page className={classes.root} title="Cierre diario">
      {renderDocumentsDialog()}
      <Container maxWidth={false}>
        <Box className={classes.navigation} display="flex">
          <Typography color="textSecondary" variant="body2">
            <Link component={RouterLink} to="" onClick={cancel}>
              Cierre diario
            </Link>
          </Typography>
        </Box>
        {renderButtonList()}
        <Card>
          <Form
            fields={formFields}
            onChangeFields={handleChangeFormFieldsValues}
          />
        </Card>
        {canShowPaymentBreakdown() && renderPaymentBreakdown()}
      </Container>
    </Page>
  );
};

export default DailyCashClosureForm;
